import { useEffect, useState } from 'react';
import { getBrowserType } from 'helpers';
import TeacherInvitedAfterMeetingAboutUs from './TeacherInvitedAfterMeetingAboutUs';
import TeacherInvitedAfterMeetingInstallExtension from './TeacherInvitedAfterMeetingInstallExtension';
import StudentInvitedReadingAnalysisPin from '../StudentInvited/StudentInvitedReadingAnalysisPin';
import TeacherInvitedAfterMeetingDone from './TeacherInvitedAfterMeetingDone';

const EXTENSION_URLS = {
  chrome: process.env.REACT_APP_CHROME_EXTENSION_URL,
  firefox: process.env.REACT_APP_FIREFOX_EXTENSION_URL,
  safari: process.env.REACT_APP_SAFARI_EXTENSION_URL,
  edge: process.env.REACT_APP_EDGE_EXTENSION_URL,
};

// A PENDING COURSE INVITATION IS A COURSE WITH invitedAsTeacher.
//
// The server builds it in getUserCourses: Courses.find({'invitedTeachers.email':
// user.email}) mapped to invitedAsTeacher: true. It is the same flag the courses
// page reads to draw the accept prompt, which is the point - the invite the
// teacher is being sent to accept is the invite this flow branches on.
//
// .some() rather than a count: a teacher can be invited to several courses at
// once and the server returns one entry per course. One is enough to have
// somewhere to be, and any number lands in the same place.
function hasPendingCourseInvite(courses) {
  return (courses || []).some((course) => course.invitedAsTeacher);
}

function TeacherInvitedAfterMeeting({ onComplete, ...props }) {
  const [step, setStep] = useState(0);
  // null while we are still asking the server. Deciding takes one request, and
  // rendering the chooser before the answer arrives would show a teacher who
  // has a course waiting the screen that says they have nothing.
  const [ending, setEnding] = useState(null);
  const { onGetUserCourses, onNavigate } = props;

  useEffect(() => {
    if (step !== 3 || ending) return undefined;
    let cancelled = false;

    (async () => {
      let courses = [];
      try {
        courses = (await onGetUserCourses()) || [];
      } catch (e) {
        // A teacher who cannot be placed still has to land somewhere. The
        // chooser always works, so an unreadable course list falls back to it
        // rather than stranding them on this screen.
        console.error('Could not read courses to place the teacher', e);
      }
      if (cancelled) return;

      if (!hasPendingCourseInvite(courses)) {
        setEnding('chooser');
        return;
      }

      // They were invited to a course, so they have somewhere to be: finish
      // the account and put them in front of the invite.
      setEnding('invited');
      await onComplete();
      if (!cancelled) onNavigate('/courses');
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  function openExtensionUrl() {
    const b = getBrowserType();
    const url =
      (b.isChrome && EXTENSION_URLS.chrome) ||
      (b.isFirefox && EXTENSION_URLS.firefox) ||
      (b.isSafari && EXTENSION_URLS.safari) ||
      (b.isEdge && EXTENSION_URLS.edge);
    if (url) window.open(url, '_blank');
  }

  if (step === 0) {
    return (
      <TeacherInvitedAfterMeetingAboutUs
        {...props}
        onContinue={() => setStep(1)}
      />
    );
  }

  if (step === 1) {
    return (
      <TeacherInvitedAfterMeetingInstallExtension
        onInstall={() => {
          openExtensionUrl();
          setStep(2);
        }}
        onSkip={() => setStep(3)}
      />
    );
  }

  if (step === 2) {
    return (
      <StudentInvitedReadingAnalysisPin
        onContinue={async () => {
          setStep(3);
        }}
      />
    );
  }

  if (ending !== 'chooser') {
    return (
      <div className="teacher-invited-about-us">
        <div className="shell">
          <div className="step-eyebrow">You're in</div>
          <h1 className="step-h1">Finishing setup…</h1>
        </div>
      </div>
    );
  }

  return (
    <TeacherInvitedAfterMeetingDone
      onCardClick={onComplete}
      onGetUserCourses={props.onGetUserCourses}
      onOpenModal={props.onOpenModal}
      onNavigate={props.onNavigate}
      user={props.user}
      courses={props.user?.courses}
    />
  );
}

export default TeacherInvitedAfterMeeting;
